import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  Box,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config/config';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
  },
  searchContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
  },
  searchInput: {
    flexGrow: 1,
  },
  activeChip: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  inactiveChip: {
    backgroundColor: '#f44336',
    color: 'white',
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  platformTypeContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const api = axios.create({
  baseURL: config.API.BASE_URL,
  timeout: config.API.TIMEOUT,
});

// Secret key for API authentication (matches backend config)
const SECRET_KEY = config.SECURITY.SECRET_KEY;

const CoinPlanPage = () => {
  const classes = useStyles();
  const [coinPlans, setCoinPlans] = useState([]);
  const [filteredCoinPlans, setFilteredCoinPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedCoinPlan, setSelectedCoinPlan] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    platformType: 0,
    productKey: '',
    dollar: '',
    coin: '',
    extraCoin: '',
    tag: '',
    isActive: true
  });

  useEffect(() => {
    fetchCoinPlans();
  }, []);

  useEffect(() => {
    // Filter coin plans based on search term
    if (searchTerm.trim() === '') {
      setFilteredCoinPlans(coinPlans);
    } else {
      const filtered = coinPlans.filter((plan) =>
        plan.product_key?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.tag?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.dollar?.toString().includes(searchTerm.toLowerCase()) ||
        plan.coin?.toString().includes(searchTerm.toLowerCase())
      );
      setFilteredCoinPlans(filtered);
    }
    setPage(0); // Reset to first page when searching
  }, [coinPlans, searchTerm]);

  const fetchCoinPlans = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(config.SECURITY.TOKEN_KEY);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const response = await api.get('/coinPlan');
      if (response.data.status) {
        setCoinPlans(response.data.coinPlans || []);
      } else {
        toast.error('Error loading coin plans');
      }
    } catch (error) {
      console.error('Error fetching coin plans:', error);
      toast.error('Error loading coin plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRefresh = () => {
    fetchCoinPlans();
  };

  const handleOpenDialog = (mode, coinPlan = null) => {
    setDialogMode(mode);
    setSelectedCoinPlan(coinPlan);
    
    if (mode === 'create') {
      setFormData({
        platformType: 0,
        productKey: '',
        dollar: '',
        coin: '',
        extraCoin: '',
        tag: '',
        isActive: true
      });
    } else if (coinPlan) {
      setFormData({
        platformType: coinPlan.platform_type || 0,
        productKey: coinPlan.product_key || '',
        dollar: coinPlan.dollar || '',
        coin: coinPlan.coin || '',
        extraCoin: coinPlan.extra_coin || '',
        tag: coinPlan.tag || '',
        isActive: coinPlan.is_active !== undefined ? coinPlan.is_active : true
      });
    }
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCoinPlan(null);
    setFormData({
      platformType: 0,
      productKey: '',
      dollar: '',
      coin: '',
      extraCoin: '',
      tag: '',
      isActive: true
    });
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem(config.SECURITY.TOKEN_KEY);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.defaults.headers.common['key'] = SECRET_KEY;
      
      const payload = {
        platformType: parseInt(formData.platformType),
        productKey: formData.productKey,
        dollar: parseFloat(formData.dollar),
        coin: parseInt(formData.coin),
        extraCoin: parseInt(formData.extraCoin) || 0,
        tag: formData.tag,
        isActive: formData.isActive
      };

      let response;
      if (dialogMode === 'create') {
        response = await api.post('/coinPlan', payload);
      } else if (dialogMode === 'edit') {
        response = await api.put(`/coinPlan/${selectedCoinPlan.id}`, payload);
      }

      if (response.data.status) {
        toast.success(`Coin plan ${dialogMode === 'create' ? 'created' : 'updated'} successfully`);
        handleCloseDialog();
        fetchCoinPlans();
      } else {
        toast.error('Error saving coin plan');
      }
    } catch (error) {
      console.error('Error saving coin plan:', error);
      toast.error('Error saving coin plan');
    }
  };

  const handleDelete = async (coinPlanId) => {
    if (window.confirm('Are you sure you want to delete this coin plan?')) {
      try {
        const token = localStorage.getItem(config.SECURITY.TOKEN_KEY);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        api.defaults.headers.common['key'] = SECRET_KEY;
        
        const response = await api.delete(`/coinPlan/${coinPlanId}`);
        if (response.data.status) {
          toast.success('Coin plan deleted successfully');
          fetchCoinPlans();
        } else {
          toast.error('Error deleting coin plan');
        }
      } catch (error) {
        console.error('Error deleting coin plan:', error);
        toast.error('Error deleting coin plan');
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (isActive) => {
    return isActive ? 
      <Chip label="Active" className={classes.activeChip} size="small" /> :
      <Chip label="Inactive" className={classes.inactiveChip} size="small" />;
  };

  const getPlatformName = (platformType) => {
    return platformType === 0 ? 'Android' : 'iOS';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const paginatedCoinPlans = filteredCoinPlans.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Coin Plans Management
      </Typography>

      <Paper className={classes.paper}>
        {/* Search and Actions */}
        <Box className={classes.searchContainer}>
          <TextField
            className={classes.searchInput}
            variant="outlined"
            size="small"
            placeholder="Search by product key, tag, dollar, or coin..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon color="action" />,
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            Add Coin Plan
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Coin Plans Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Platform</TableCell>
                <TableCell>Product Key</TableCell>
                <TableCell>Dollar</TableCell>
                <TableCell>Coin</TableCell>
                <TableCell>Extra Coin</TableCell>
                <TableCell>Tag</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Loading coin plans...
                  </TableCell>
                </TableRow>
              ) : paginatedCoinPlans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No coin plans found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCoinPlans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell>{getPlatformName(plan.platform_type)}</TableCell>
                    <TableCell>{plan.product_key || 'N/A'}</TableCell>
                    <TableCell>${plan.dollar || 0}</TableCell>
                    <TableCell>{plan.coin || 0}</TableCell>
                    <TableCell>{plan.extra_coin || 0}</TableCell>
                    <TableCell>{plan.tag || 'N/A'}</TableCell>
                    <TableCell>{getStatusChip(plan.is_active)}</TableCell>
                    <TableCell>{formatDate(plan.created_at)}</TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog('view', plan)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog('edit', plan)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(plan.id)}
                          color="secondary"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredCoinPlans.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Dialog for Create/Edit/View */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create Coin Plan' : 
           dialogMode === 'edit' ? 'Edit Coin Plan' : 'View Coin Plan'}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth className={classes.formField}>
                <InputLabel>Platform Type</InputLabel>
                <Select
                  name="platformType"
                  value={formData.platformType}
                  onChange={handleInputChange}
                  disabled={dialogMode === 'view'}
                >
                  <MenuItem value={0}>Android</MenuItem>
                  <MenuItem value={1}>iOS</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Product Key"
                name="productKey"
                value={formData.productKey}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                className={classes.formField}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Dollar"
                name="dollar"
                type="number"
                inputProps={{ step: 0.01 }}
                value={formData.dollar}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                className={classes.formField}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Coin"
                name="coin"
                type="number"
                value={formData.coin}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                className={classes.formField}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Extra Coin"
                name="extraCoin"
                type="number"
                value={formData.extraCoin}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                className={classes.formField}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tag"
                name="tag"
                value={formData.tag}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                className={classes.formField}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    name="isActive"
                    disabled={dialogMode === 'view'}
                  />
                }
                label="Is Active"
              />
            </Grid>
            
            {dialogMode === 'view' && selectedCoinPlan && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Created At"
                    value={formatDate(selectedCoinPlan.created_at)}
                    disabled
                    className={classes.formField}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Updated At"
                    value={formatDate(selectedCoinPlan.updated_at)}
                    disabled
                    className={classes.formField}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          {dialogMode !== 'view' && (
            <Button onClick={handleSubmit} color="primary" variant="contained">
              {dialogMode === 'create' ? 'Create' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CoinPlanPage;